/**
 * Conditionally render children based on the caller's Edna role.
 *
 * Wraps controls the API would refuse, so users are not shown buttons that
 * cannot work. This is a UX affordance, **not** an enforcement boundary — the
 * api-edna service independently authorizes every request, and a gated action
 * reached some other way still returns 403.
 *
 *     <RoleGate minRole={GATES.hierarchyWrite}>
 *       <Button onClick={handleDelete}>Delete</Button>
 *     </RoleGate>
 *
 * ## Renders nothing while the role is unknown
 *
 * Not a spinner, not a disabled control — nothing. Three reasons:
 *
 * - A spinner where a Delete button will appear is worse than an absence.
 * - A control that pops in a moment after load invites a misclick.
 * - A *disabled* button prompts "why can't I?"; an absent one does not raise
 *   the question.
 *
 * Screens gated in their entirety (Monitoring, User Management) should NOT use
 * this — they want a loading state, because the alternative is flashing
 * access-denied at a user who does have access. Use `useCallerRole()` directly
 * and branch on `status`.
 */

import type { ReactNode } from 'react';

import { useCallerRole } from '../../auth/useCallerRole';
import type { EdnaRole } from '../../auth/roles';

interface RoleGateProps {
  /** Minimum role required. Use a `GATES` value rather than a literal. */
  minRole: EdnaRole;
  children: ReactNode;
  /**
   * Rendered instead of `children` when the caller is below `minRole`.
   *
   * Defaults to nothing, which is almost always right. Supply this only when the
   * absence would leave a confusing hole in a layout — never to explain the
   * denial, which just draws attention to a control the user cannot use.
   */
  fallback?: ReactNode;
}

export function RoleGate({ minRole, children, fallback = null }: RoleGateProps) {
  const { atLeast } = useCallerRole();

  // `atLeast` is false while loading and on failure, so both hide the children
  // without a separate branch here.
  return <>{atLeast(minRole) ? children : fallback}</>;
}
